import React, { useState } from 'react';
import { t } from '@lingui/macro';

import FlatButton from '@rover/components/src/OverflowTextModal/FlatButton';
import Modal from '@rover/components/src/OverflowTextModal/Modal';
import { Box, Flex, Heading } from '@rover/kibble/core';
import IconBlock from '@rover/kibble/patterns/IconBlock/IconBlock';
import type { Attribute } from '@rover/rsdk/src/apiClient/latest/model/attribute';

import { sendSectionInteractionEvent } from '../../../utils/sendSectionInteractionEvent';
import { renderIcon } from '../../../utils/utils';

const AttributeBlock = ({ attribute }: { attribute: Attribute }): JSX.Element => {
  const icon = renderIcon(attribute.icon);
  return (
    <Box py="2x" minWidth={['none', '343px']}>
      <IconBlock
        iconSize="24px"
        size="200"
        fontWeight="regular"
        icon={icon}
        title={attribute.text}
      />
    </Box>
  );
};

type Props = {
  attributes: Attribute[];
};

const HomeSection = ({ attributes }: Props): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const firstColumnAttributes = attributes.slice(0, 4);
  const secondColumnAttributes = attributes.slice(4, 8);

  const handleOpenModal = (event): void => {
    event.preventDefault();
    sendSectionInteractionEvent('about-members-home');
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
  };

  const seeAllButton = <FlatButton onClick={handleOpenModal}>{t`See all`}</FlatButton>;

  return (
    <Flex flexDirection="column" width="100%" data-testid="home-section">
      <Heading size="300">
        {t({
          id: 'Home.residence',
          message: 'Home',
          comment: 'Home in the context of a residence or physical dwelling',
        })}
      </Heading>
      <Flex>
        <Flex flex="1" flexDirection="column" alignItems="flex-start" sx={{ marginRight: '56px' }}>
          {firstColumnAttributes.map((attribute) => (
            <AttributeBlock attribute={attribute} key={attribute.key} />
          ))}
        </Flex>
        <Flex
          display={['none', 'flex', 'none', 'flex']}
          flex="1"
          flexDirection="column"
          alignItems="flex-start"
        >
          {secondColumnAttributes.map((attribute) => (
            <AttributeBlock attribute={attribute} key={attribute.key} />
          ))}
        </Flex>
      </Flex>
      {attributes.length > 4 && (
        <Box display={['initial', 'none', 'initial', 'none']} mt="2x">
          {seeAllButton}
        </Box>
      )}
      {attributes.length > 8 && (
        <Box display={['none', 'initial', 'none', 'initial']} mt="2x">
          {seeAllButton}
        </Box>
      )}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <Box width="100%">
          <Heading size="300">
            {t({
              id: 'Home.residence',
              message: 'Home',
              comment: 'Home in the context of a residence or physical dwelling',
            })}
          </Heading>
          {attributes.map((attribute) => (
            <AttributeBlock attribute={attribute} key={attribute.key} />
          ))}
        </Box>
      </Modal>
    </Flex>
  );
};
export default HomeSection;
